import { useRouter } from 'next/router'
import React, { FC, MouseEventHandler } from 'react'
import ReactImageGallery from 'react-image-gallery'

import useIsOnMobile from '../../services/useIsOnMobile'
import { ScreenSize } from '../../utils/constants'
import { MediaType } from '../../utils/types/Product'
import CloseImageFullscreenIcon from '../Icons/ImageGallery/CloseImageFullscreenIcon'
import FullscreenIcon from '../Icons/ImageGallery/FullscreenIcon'
import ThumbnailsDownIcon from '../Icons/ImageGallery/ThumbnailsDownIcon'
import ThumbnailsUpIcon from '../Icons/ImageGallery/ThumbnailsUpIcon'
import ImageMagnify from './ImageMagnify'

import styles from '../../styles/Product.module.less'

interface CustomFullscreenButtonProps {
  toggleFullscreen: React.MouseEventHandler<HTMLElement>
  isFullscreen: boolean
}

interface ProductImageProps {
  media: MediaType[]
}

const CustomFullscreenButton: React.FC<CustomFullscreenButtonProps> = (props) => {
  const { toggleFullscreen, isFullscreen } = props

  const onFullscreenToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    window.onhashchange = () => {
      if (!isFullscreen) toggleFullscreen(e)
    }
    toggleFullscreen(e)
  }

  return (
    <button
      type="button"
      className={`
        image-gallery-icon image-gallery-fullscreen-button
        ${isFullscreen ? 'image-gallery-close-fullscreen' : 'image-gallery-open-fullscreen'}
        `}
      onClick={onFullscreenToggle}
      aria-label="Open Fullscreen"
    >
      {isFullscreen ? <CloseImageFullscreenIcon /> : <FullscreenIcon /> }
    </button>
  )
}

const ProductImage: FC<ProductImageProps> = (props) => {
  const { media } = props
  const { asPath, ...router } = useRouter()
  const showThumbnailsNav = media.length > 4

  const isScreenMd = useIsOnMobile(ScreenSize.md)
  const isScreenSm = useIsOnMobile(ScreenSize.sm)

  const appendUrlWhenFullscreen = (isFullscreen: boolean) => {
    if (isFullscreen) router.push('#fullscreen')
    else router.push(asPath.replace('#fullscreen', ''))
  }

  const mobileMedia = media.map((img) => (
    { ...img, thumbnailHeight: isScreenSm ? 80 : 100, originalHeight: 350 }
  ))

  const renderThumbnailNavButton = (
    onClick: MouseEventHandler<HTMLElement>,
    disabled: boolean,
    direction: 'up' | 'down',
  ) => (
    <button
      className={`thumbnails-nav-button thumbnails-${direction} ${disabled ? 'disabled' : ''}`}
      type="button"
      onClick={onClick}
    >
      {direction === 'up' ? <ThumbnailsUpIcon /> : <ThumbnailsDownIcon />}
    </button>
  )

  return (
    <div className={styles['gallery-container']}>
      <ReactImageGallery
        key={isScreenMd ? 'horizontal' : 'vertical'}
        slideVertically={!isScreenMd}
        showThumbnailsNav={showThumbnailsNav}
        infinite={false}
        renderThumbnailsTopNav={(onClick, disabled) => renderThumbnailNavButton(onClick, disabled, 'up')}
        renderThumbnailsBottomNav={(onClick, disabled) => renderThumbnailNavButton(onClick, disabled, 'down')}
        additionalClass={`product ${showThumbnailsNav ? 'show-thumbnails-nav' : ''}`}
        items={isScreenMd ? mobileMedia ?? [] : media ?? []}
        renderItem={!isScreenMd ? ImageMagnify : undefined}
        showPlayButton={false}
        showNav={false}
        slideOnThumbnailOver={false}
        thumbnailPosition={isScreenMd ? 'bottom' : 'left'}
        useBrowserFullscreen={false}
        onScreenChange={(isFullscreen) => appendUrlWhenFullscreen(isFullscreen)}
        renderFullscreenButton={
        (onClick, isFullscreen) => (
          <CustomFullscreenButton
            toggleFullscreen={onClick}
            isFullscreen={isFullscreen}
          />
        )
      }
      />
    </div>
  )
}

export default ProductImage
